<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("menu.seller_orders") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button size="sm" type="neutral" @click="openCutOff">
                        <i class="fa fa-box"></i>
                        {{ $t("seller_orders.btn_cut_off") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-12">
                            <h3 class="mb-0">{{ $t("menu.seller_orders") }}</h3>
                        </div>
                    </div>
                </template>

                <seller-tracking-recovery ref="trackingRecovery" @changed="search"></seller-tracking-recovery>

                <!-- ============ Status tabs ============ -->
                <!-- design 2026-10-05 §1. The channel tabs are gone: the channel
                     is a badge column + a filter in the panel. Counts come from
                     `tab_counts` in the same list response; when the BE does not
                     send them (null) no number is drawn rather than a fake 0. -->
                <div class="px-4">
                    <el-tabs v-model="tab" class="seller-status-tabs" @tab-click="onTabClick">
                        <el-tab-pane v-for="key in tabKeys" :key="key" :name="key">
                            <span slot="label">
                                {{ $t(tabLabelKey(key)) }}
                                <span v-if="tabCounts" class="tab-count">{{ formatTabCount(tabCounts[key]) }}</span>
                            </span>
                        </el-tab-pane>
                    </el-tabs>
                </div>

                <!-- ============ Filter toolbar ============ -->
                <!-- design 2026-10-05 §5.1 item 2: filter button · active chips ·
                     reset · found-N. Every chip removes one field and reloads. -->
                <div class="px-4 pb-3 d-flex flex-wrap align-items-center seller-filter-toolbar">
                    <base-button size="sm" type="secondary" class="mr-2 mb-2" @click="openFilter">
                        <i class="fa fa-sliders-h"></i>
                        {{ $t("seller_orders.btn_filter") }}<template v-if="activeChipList.length"> ({{ activeChipList.length }})</template>
                    </base-button>
                    <!-- Always say which window the list + ready-to-cut counts
                         cover: all-time when no date range is set. With a
                         range, the closable date chip below names it. -->
                    <el-tooltip v-if="periodChip" :content="$t('seller_orders.period_hint')" placement="top">
                        <el-tag size="medium" type="info" class="mr-2 mb-2 seller-period-chip">
                            <i class="fa fa-calendar-alt"></i>
                            {{ $t(periodChip.labelKey) }}
                        </el-tag>
                    </el-tooltip>
                    <el-tag
                        v-for="chip in activeChipList"
                        :key="chip.key"
                        closable
                        size="medium"
                        type="info"
                        class="mr-2 mb-2"
                        @close="onRemoveChip(chip.key)"
                    >
                        {{ $t(chip.labelKey) }}: {{ chip.text }}
                    </el-tag>
                    <el-tooltip v-if="activeChipList.length" :content="$t('seller_orders.filter_reset')" placement="top">
                        <base-button size="sm" type="secondary" class="mr-2 mb-2" @click="resetFilters">
                            <i class="fa fa-undo"></i>
                        </base-button>
                    </el-tooltip>
                    <span class="text-muted mb-2 seller-found-n">{{ $t("seller_orders.found_n", { n: total }) }}</span>
                </div>

                <!-- ============ Ready-to-cut count chips ============ -->
                <!-- design 2026-10-04 §6.1. The numbers come from the SAME list
                     response as the table, counted with the same look-back
                     window the cut will use, so the chip total is the number of
                     orders the round will really move. -->
                <div class="px-4 pb-3 seller-round-chips">
                    <span class="text-muted mr-2">{{ $t("seller_orders.cutoff_total") }}:</span>
                    <template v-if="preview.present">
                        <el-tooltip
                            v-for="chip in previewChipList"
                            :key="chip.key"
                            :disabled="!chip.hintKey"
                            :content="chip.hintKey ? $t(chip.hintKey) : ''"
                            placement="top"
                        >
                            <el-tag
                                size="medium"
                                class="mr-2"
                                :type="chip.type"
                                :class="{ 'font-weight-bold': chip.key === 'total' }"
                            >
                                {{ $t(chip.labelKey) }}<template v-if="chip.count !== null"> {{ chip.count }}</template>
                            </el-tag>
                        </el-tooltip>
                    </template>
                    <el-tag v-else size="medium" type="info" class="mr-2">&mdash;</el-tag>
                </div>

                <!-- ============ Table ============ -->
                <div v-if="truncatedWms" class="px-4 pb-3">
                    <el-alert type="warning" :closable="false" :title="$t('seller_orders.wms_truncated')"></el-alert>
                </div>
                <div v-loading="loading">
                    <el-table
                        ref="ordersTable"
                        class="so-orders-table"
                        :data="rows"
                        row-key="uuid"
                        header-row-class-name="thead-light"
                        :empty-text="emptyText"
                        @expand-change="onExpandChange"
                    >
                        <template slot="empty">
                            <div class="py-3">
                                <div>{{ emptyText }}</div>
                                <base-button
                                    v-if="activeChipList.length"
                                    size="sm"
                                    type="secondary"
                                    class="mt-2"
                                    @click="resetFilters"
                                >
                                    {{ $t("seller_orders.filter_reset") }}
                                </base-button>
                            </div>
                        </template>
                        <!-- Owner 2026-10-15: the order detail opens INLINE under the row (the
                             old detail dialog is gone). The expand column is 1px with its arrow
                             hidden (CSS below); the order number toggles it. Several rows may be
                             open at once, like the BO orders overview (OrdersNew.vue). -->
                        <el-table-column type="expand" width="1" class-name="so-expand-col">
                            <template slot-scope="{ row }">
                                <div class="so-detail-sticky" :style="detailPanelStyle">
                                    <seller-order-detail
                                        :row="row"
                                        :detail="detailByUuid[row.uuid] || null"
                                        :loading="!!detailLoading[row.uuid]"
                                        :error="detailError[row.uuid] || ''"
                                        :api-base="apiBase"
                                        :payment-type-text="paymentTypeOf(row)"
                                        :pay-status-text="payStatusLabel(row.payment_status)"
                                        :pay-status-class="payStatusClass(row.payment_status)"
                                        :can-cancel="canCancelRow(row)"
                                        @close="closeDetail(row)"
                                        @cancel="openCancel(row)"
                                        @retry="loadDetail(row)"
                                        @copy="copyText"
                                    ></seller-order-detail>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('seller_orders.col_order')" min-width="170px">
                            <template slot-scope="{ row }">
                                <a
                                    href="#"
                                    class="font-weight-bold so-order-link"
                                    data-testid="so-order-toggle"
                                    :aria-expanded="isExpanded(row) ? 'true' : 'false'"
                                    @click.prevent="toggleDetail(row)"
                                >{{ row.order_no || shortUuid(row.uuid) }}<i
                                    class="fa ml-1 so-order-chevron"
                                    :class="isExpanded(row) ? 'fa-chevron-up' : 'fa-chevron-down'"
                                ></i></a>
                                <i
                                    class="fa fa-copy text-muted ml-2"
                                    style="cursor: pointer"
                                    @click="copyUuid(row.uuid)"
                                ></i>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('seller_orders.col_channel')" min-width="150px">
                            <template slot-scope="{ row }">
                                <span class="badge so-badge-lg" :class="channelBadgeOf(row).badgeClass">
                                    {{ $t(channelBadgeOf(row).labelKey) }}
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('seller_orders.col_recipient')" min-width="150px">
                            <template slot-scope="{ row }">
                                <div>{{ row.recipient_name || "-" }}</div>
                                <small class="text-muted">{{ row.recipient_phone }}</small>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('seller_orders.col_status')" min-width="160px">
                            <!-- spec 2026-10-16 seller-order-cancel-actor-reason §5: a cancelled
                                 row names who cancelled (customer / staff + name / auto); the
                                 tooltip has the reason type, reason/note and time. No `cancel`
                                 object (older API) → the plain status badge. -->
                            <template slot-scope="{ row }">
                                <el-tooltip
                                    v-if="cancelBadgeOf(row)"
                                    :disabled="!cancelTooltipOf(row)"
                                    :content="cancelTooltipOf(row) || '-'"
                                    placement="top"
                                >
                                    <span
                                        class="badge so-badge-lg so-cancel-badge"
                                        :class="cancelBadgeOf(row).badgeClass"
                                        data-testid="so-cancel-badge"
                                    >{{ cancelBadgeOf(row).text }}</span>
                                </el-tooltip>
                                <span v-else class="badge so-badge-lg" :class="statusBadgeClass(row.order_status)">{{ orderStatusLabel(row.order_status) }}</span>
                            </template>
                        </el-table-column>
                        <!-- Owner 2026-10-15: every tracking number of the order (one per
                             package), each copyable like the BO orders pages; "-" when none. -->
                        <el-table-column :label="$t('seller_orders.col_tracking')" min-width="160px">
                            <template slot-scope="{ row }">
                                <template v-if="trackingNumbersOf(row).length">
                                    <div v-for="tn in trackingNumbersOf(row)" :key="tn" class="so-tracking" data-testid="so-tracking">
                                        {{ tn }}<button
                                            type="button"
                                            class="btn btn-link p-0 ml-1 so-copy"
                                            data-testid="so-copy-tracking"
                                            :title="$t('seller_orders.copy')"
                                            :aria-label="$t('seller_orders.copy')"
                                            @click.stop="copyText(tn)"
                                        ><i class="fa fa-copy"></i></button>
                                    </div>
                                    <small v-if="row.shipping_provider" class="text-muted">{{ row.shipping_provider }}</small>
                                </template>
                                <span v-else class="text-muted">-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('seller_orders.col_payment_type')" min-width="130px" class-name="so-words">
                            <template slot-scope="{ row }">
                                {{ paymentTypeOf(row) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('seller_orders.col_payment_status')" min-width="140px">
                            <template slot-scope="{ row }">
                                <span class="badge so-badge-lg" :class="payStatusClass(row.payment_status)">
                                    {{ payStatusLabel(row.payment_status) }}
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('seller_orders.col_amount')" min-width="110px" align="right">
                            <template slot-scope="{ row }">{{ formatAmount(row.pay_amount) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('seller_orders.col_placed_at')" min-width="160px">
                            <template slot-scope="{ row }">{{ formatTime(row.created_at) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('seller_orders.col_packing_round')" min-width="110px">
                            <template slot-scope="{ row }">
                                <span
                                    v-if="row.bo_packing_id"
                                    class="badge badge-primary"
                                    style="cursor: pointer"
                                    @click="goPackingRoom(row.bo_packing_id)"
                                >
                                    #{{ row.bo_packing_id }}
                                </span>
                                <span v-else class="text-muted">-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('seller_orders.col_bo')" min-width="130px">
                            <template slot-scope="{ row }">
                                <span v-if="row.bo_order_id" class="badge badge-success so-badge-lg">
                                    #{{ row.bo_order_id }}
                                </span>
                                <span v-else class="text-muted">-</span>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <!-- ============ Pagination ============ -->
                <div class="px-4 py-3 d-flex justify-content-end align-items-center">
                    <el-pagination
                        background
                        layout="prev, pager, next"
                        :total="total"
                        :page-size="limit"
                        :current-page.sync="page"
                        @current-change="onPageChange"
                    ></el-pagination>
                </div>
            </card>
        </div>

        <!-- ============ Filter panel (right side) ============ -->
        <!-- design 2026-10-05 §5.2. Element UI here is 2.4.11 — no el-drawer —
             so this is an el-dialog pinned to the right edge by its
             custom-class (CSS at the bottom, unscoped because the dialog is
             appended to <body>). The panel edits a COPY (`draft`); only
             "apply" copies it into the live filters. Keep the packing-round
             word out of this block: the packing-round tests find that popup
             by it. -->
        <el-dialog
            :visible.sync="filterVisible"
            custom-class="seller-filter-drawer"
            :title="$t('seller_orders.filter_title')"
            :close-on-click-modal="true"
            :append-to-body="true"
            width="420px"
        >
            <div class="seller-filter-fields">
                <div class="sf-field">
                    <div class="sf-label">{{ $t("seller_orders.f_sub_status") }}</div>
                    <el-radio-group v-model="draft.status" class="sf-radios">
                        <el-radio label="">{{ $t("seller_orders.all") }}</el-radio>
                        <el-radio v-for="code in subStatusCodes" :key="code" :label="String(code)">
                            {{ orderStatusLabel(code) }}<template v-if="statusCounts"> ({{ formatTabCount(statusCounts[code] || 0) }})</template>
                        </el-radio>
                    </el-radio-group>
                </div>

                <div class="sf-field">
                    <div class="sf-label">{{ $t("seller_orders.f_date_type") }}</div>
                    <!-- spec 2026-10-07 §11.2 R15: type and range on separate lines (same fix as the BO orders page). -->
                    <el-select v-model="draft.date_type" class="sf-date-type w-100 mb-2">
                        <el-option
                            v-for="d in dateTypes"
                            :key="d.value"
                            :label="$t(d.labelKey)"
                            :value="d.value"
                        ></el-option>
                    </el-select>
                    <el-date-picker
                        v-model="draftRange"
                        type="daterange"
                        value-format="yyyy-MM-dd"
                        :clearable="true"
                        class="sf-date-range"
                        style="width: 100%"
                    ></el-date-picker>
                </div>

                <div class="sf-field">
                    <div class="sf-label">{{ $t("seller_orders.f_order_q") }}</div>
                    <el-input
                        v-model="draft.order_q"
                        clearable
                        :maxlength="maxTextLen"
                        :placeholder="$t('seller_orders.f_order_q')"
                        @keyup.enter.native="applyFilter"
                    ></el-input>
                </div>
                <div class="sf-field">
                    <div class="sf-label">{{ $t("seller_orders.f_customer_q") }}</div>
                    <el-input
                        v-model="draft.customer_q"
                        clearable
                        :maxlength="maxTextLen"
                        :placeholder="$t('seller_orders.f_customer_q')"
                        @keyup.enter.native="applyFilter"
                    ></el-input>
                </div>
                <div class="sf-field">
                    <div class="sf-label">{{ $t("seller_orders.f_product_q") }}</div>
                    <el-input
                        v-model="draft.product_q"
                        clearable
                        :maxlength="maxTextLen"
                        :placeholder="$t('seller_orders.f_product_q')"
                        @keyup.enter.native="applyFilter"
                    ></el-input>
                </div>

                <div class="sf-field">
                    <div class="sf-label">{{ $t("seller_orders.f_channels") }}</div>
                    <el-select
                        v-model="draft.channels"
                        multiple
                        clearable
                        style="width: 100%"
                        :placeholder="$t('seller_orders.f_channels')"
                    >
                        <el-option
                            v-for="c in channelOptions"
                            :key="c.value"
                            :label="$t(c.labelKey)"
                            :value="c.value"
                        ></el-option>
                    </el-select>
                </div>

                <div class="sf-field">
                    <div class="sf-label">{{ $t("seller_orders.filter_payment_type") }}</div>
                    <el-select v-model="draft.payment_method_type" style="width: 100%">
                        <el-option
                            v-for="option in paymentTypeChoices"
                            :key="option.id"
                            :label="option.name"
                            :value="option.id"
                        ></el-option>
                    </el-select>
                </div>

                <div class="sf-field">
                    <div class="sf-label">{{ $t("seller_orders.f_pay_state") }}</div>
                    <el-select
                        v-model="draft.pay_state"
                        clearable
                        style="width: 100%"
                        :placeholder="$t('seller_orders.f_pay_state')"
                    >
                        <el-option
                            v-for="p in payStates"
                            :key="p.value"
                            :label="$t(p.labelKey)"
                            :value="p.value"
                        ></el-option>
                    </el-select>
                </div>

                <div class="sf-field">
                    <div class="sf-label">{{ $t("seller_orders.f_shipping") }}</div>
                    <el-select
                        v-model="draft.shipping_providers"
                        multiple
                        filterable
                        clearable
                        style="width: 100%"
                        :loading="facetsLoading"
                        :placeholder="$t('seller_orders.f_shipping')"
                    >
                        <el-option v-for="name in providerOptions" :key="name" :label="name" :value="name"></el-option>
                    </el-select>
                </div>

                <div class="sf-field">
                    <div class="sf-label">{{ $t("seller_orders.f_packing") }}</div>
                    <el-radio-group v-model="draft.packing" class="sf-radios sf-radios-inline">
                        <el-radio label="">{{ $t("seller_orders.all") }}</el-radio>
                        <el-radio v-for="p in packingStates" :key="p.value" :label="p.value">
                            {{ $t(p.labelKey) }}
                        </el-radio>
                    </el-radio-group>
                </div>

                <div class="sf-field">
                    <el-checkbox v-model="draft.wms_unmapped">{{ $t("seller_orders.f_wms_unmapped") }}</el-checkbox>
                </div>
            </div>
            <div slot="footer" class="d-flex align-items-center">
                <base-button outline type="default" class="mr-auto" @click="clearDraft">
                    {{ $t("seller_orders.filter_clear_all") }}
                </base-button>
                <base-button type="secondary" @click="filterVisible = false">
                    {{ $t("seller_orders.btn_cancel") }}
                </base-button>
                <base-button type="primary" @click="applyFilter">
                    {{ $t("seller_orders.filter_apply") }}
                </base-button>
            </div>
        </el-dialog>

        <!-- ============ Cancel one order (spec 2026-10-16 §5) ============ -->
        <seller-order-cancel-dialog
            :visible.sync="cancelVisible"
            :order="cancelTarget"
            :submitting="cancelSubmitting"
            @submit="onCancelSubmit"
        ></seller-order-cancel-dialog>

        <!-- ============ Cross-channel packing round modal ============ -->
        <!-- design 2026-10-04 §6.4. Deliberately has NO channel picker and NO
             carrier picker: the round is selected by platform_id = 9 (every
             order from web / member / app) and the pack room assigns the
             carrier. Do not reintroduce either select here. -->
        <modal :show.sync="cutOffVisible" modal-classes="modal-dialog-centered">
            <h5 slot="header" class="modal-title">{{ $t("seller_orders.cutoff_title_all") }}</h5>
            <div>
                <el-alert
                    type="info"
                    :closable="false"
                    :title="$t('seller_orders.cutoff_scope_note')"
                    class="mb-3"
                ></el-alert>
                <div class="form-group">
                    <label class="form-control-label">{{ $t("seller_orders.cutoff_back_days_label") }}</label>
                    <el-select v-model="cutOff.back_day" style="width: 100%" @change="onCutOffParamsChange">
                        <el-option
                            v-for="d in lookbackOptions"
                            :key="d"
                            :label="String(d)"
                            :value="d"
                        ></el-option>
                    </el-select>
                </div>
                <div class="form-group">
                    <label class="form-control-label">{{ $t("seller_orders.cutoff_payment_type") }}</label>
                    <el-select
                        v-model="cutOff.bo_payment_method_type_id"
                        style="width: 100%"
                        @change="onCutOffParamsChange"
                    >
                        <el-option
                            v-for="option in paymentTypeChoices"
                            :key="option.id"
                            :label="option.name"
                            :value="option.id"
                        ></el-option>
                    </el-select>
                </div>
                <div v-loading="previewLoading">
                    <table class="table table-sm mb-2">
                        <thead class="thead-light">
                            <tr>
                                <th>{{ $t("seller_orders.cutoff_table_channel") }}</th>
                                <th class="text-right">{{ $t("seller_orders.cutoff_table_count") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="row in cutOffRows"
                                :key="row.key"
                                :class="{ 'font-weight-bold': row.total }"
                            >
                                <td>{{ $t(row.labelKey) }}</td>
                                <td class="text-right">
                                    <span v-if="row.count !== null">{{ row.count }}</span>
                                    <span v-else class="text-muted">&mdash;</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <el-alert
                        v-if="cutOffPreview.noWmsSummaryCount > 0"
                        type="warning"
                        :closable="false"
                        :title="$t('seller_orders.cutoff_warn_no_wms', { cnt: cutOffPreview.noWmsSummaryCount })"
                        class="mb-2"
                    ></el-alert>
                    <p v-if="cutOffTotal === 0" class="text-muted mb-0">
                        {{ $t("seller_orders.cutoff_none") }}
                    </p>
                </div>
            </div>
            <template slot="footer">
                <base-button type="secondary" @click="cutOffVisible = false">
                    {{ $t("seller_orders.btn_cancel") }}
                </base-button>
                <base-button
                    type="primary"
                    :disabled="cutOffLoading || previewLoading || cutOffTotal === 0"
                    @click="submitCutOff"
                >
                    {{ $t("seller_orders.btn_confirm_cut") }} ({{ cutOffTotal }})
                </base-button>
            </template>
        </modal>
    </div>
</template>

<script>
import {
    Table,
    TableColumn,
    DatePicker,
    Select,
    Option,
    Input,
    Tabs,
    TabPane,
    Dialog,
    Pagination,
    Alert,
    Tag,
    Tooltip,
    Radio,
    RadioGroup,
    Checkbox
} from "element-ui";
import { Modal } from "@/components";
import SellerTrackingRecovery from "@/components/Packing/SellerTrackingRecovery.vue";
import SellerOrderDetail from "@/views/Seller/SellerOrderDetail.vue";
import SellerOrderCancelDialog from "@/views/Seller/SellerOrderCancelDialog.vue";
import { SellerService } from "@/resource";
import { apiErrorMessage } from "@/util/api-error";
import {
    LOOKBACK_OPTIONS,
    DEFAULT_BACK_DAY,
    PAYMENT_TYPE_ALL,
    channelBadge,
    clampBackDay,
    normalizePreview,
    packingRoundErrorKey,
    packingRoundPayload,
    packingRoundResult,
    paymentTypeOptions,
    previewChips,
    previewFromListResponse,
    previewQueryParams,
    previewRows,
    successMessageArgs
} from "@/utils/sellerPackingRound";
import {
    TAB_KEYS,
    DEFAULT_TAB,
    CHANNEL_OPTIONS,
    DATE_TYPES,
    PAY_STATES,
    PACKING_STATES,
    MAX_TEXT_LEN,
    activeChips,
    apiParams,
    cloneFilters,
    emptyFilters,
    filtersFromQuery,
    formatTabCount,
    normalizeStatusCounts,
    normalizeTabCounts,
    periodChip as periodChipOf,
    queryFromFilters,
    removeChip,
    statusBadgeClass,
    subStatusOptions
} from "@/utils/sellerOrderFilters";
import { ORDERS_DEFAULT_DAYS, ORDER_SEARCH_PARAMS, shouldApplyDefaultDate, withDefaultDateRange } from "@/utils/defaultDateRange";
import { sellerPaymentChannelLabel, sellerPaymentTypeLabel, trackingNumbersOf } from "@/utils/sellerOrderDetail";
import { clipboardText, copyToClipboard } from "@/utils/clipboard";
import {
    cancelBadge,
    cancelPayload,
    cancelView,
    canSellerCancel,
    emptyCancelForm,
    validateCancelForm
} from "@/utils/orderCancelReason";
import swal from "sweetalert2";

// Keystroke-to-request damping for the modal's two selects (design §3 step 5).
const PREVIEW_DEBOUNCE_MS = 300;

export default {
    name: "SellerOrders",
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [DatePicker.name]: DatePicker,
        [Select.name]: Select,
        [Option.name]: Option,
        [Input.name]: Input,
        [Tabs.name]: Tabs,
        [TabPane.name]: TabPane,
        [Dialog.name]: Dialog,
        [Pagination.name]: Pagination,
        [Alert.name]: Alert,
        [Tag.name]: Tag,
        [Tooltip.name]: Tooltip,
        [Radio.name]: Radio,
        [RadioGroup.name]: RadioGroup,
        [Checkbox.name]: Checkbox,
        Modal,
        SellerTrackingRecovery,
        SellerOrderDetail,
        SellerOrderCancelDialog
    },
    data() {
        return {
            loading: false,
            // Status tab (design 2026-10-05 §1); restored from the URL in created().
            tab: DEFAULT_TAB,
            loadedTab: DEFAULT_TAB,
            tabKeys: TAB_KEYS,
            // Applied filters. payment_method_type is the SAME param the
            // packing-round chips are counted with, so it stays single-select
            // and defaults to "all" (design 2026-10-05 §2 item 6).
            filters: Object.assign(emptyFilters(), { payment_method_type: PAYMENT_TYPE_ALL }),
            // The panel's working copy — "cancel" throws it away.
            draft: emptyFilters(),
            filterVisible: false,
            tabCounts: null,
            statusCounts: null,
            truncatedWms: false,
            facetProviders: [],
            facetsLoaded: false,
            facetsLoading: false,
            channelOptions: CHANNEL_OPTIONS,
            dateTypes: DATE_TYPES,
            payStates: PAY_STATES,
            packingStates: PACKING_STATES,
            maxTextLen: MAX_TEXT_LEN,
            rows: [],
            total: 0,
            page: 1,
            limit: 20,
            // Ready-to-cut counts for the chip row, from the list response.
            preview: normalizePreview(null, { present: false }),
            // Inline order detail (owner 2026-10-15), keyed by uuid: the
            // GET /seller/orders/:uuid data, in-flight flags, error text. A
            // reload of the list drops all three (fetchSeq ignores late answers).
            detailByUuid: {},
            detailLoading: {},
            detailError: {},
            expandedUuids: [],
            fetchSeq: 0,
            tableViewWidth: 0,
            apiBase: process.env.VUE_APP_API_BASE_URL || "",
            // Cancel dialog (spec 2026-10-16 §5): the row, the submitted form.
            cancelVisible: false,
            cancelTarget: null,
            cancelForm: emptyCancelForm(),
            cancelSubmitting: false,
            // Packing-round modal
            cutOffVisible: false,
            cutOffLoading: false,
            previewLoading: false,
            previewTimer: null,
            cutOffPreview: normalizePreview(null, { present: false }),
            cutOff: {
                back_day: DEFAULT_BACK_DAY,
                bo_payment_method_type_id: PAYMENT_TYPE_ALL
            },
            lookbackOptions: LOOKBACK_OPTIONS
        };
    },
    computed: {
        // mw.IsFullOrder: the seller cancel POST's gate (it answers 401 = logout
        // for a partial role, so the button must not render without it).
        isFullOrder() {
            return !!(this.$store && this.$store.getters["Auth/isFullOrder"]);
        },
        // The expanded panel spans the visible part of the (horizontally
        // scrollable) table, not the whole scroll width (same as OrdersNew.vue).
        detailPanelStyle() {
            return this.tableViewWidth > 0 ? { width: this.tableViewWidth + "px" } : null;
        },
        // Sub-status radios for the current tab (design 2026-10-05 §5.2).
        subStatusCodes() {
            return subStatusOptions(this.tab);
        },
        // Leading "ช่วงเวลา: ทั้งหมด" tag; null when a date range is set.
        periodChip() {
            return periodChipOf(this.filters);
        },
        activeChipList() {
            return activeChips(this.filters, {
                t: key => this.$t(key),
                paymentType: id => this.paymentTypeLabel(id)
            });
        },
        emptyText() {
            return this.activeChipList.length
                ? this.$t("seller_orders.empty_filtered")
                : this.$t("seller_orders.empty_tab");
        },
        // el-date-picker daterange wants an array; the model keeps two strings.
        draftRange: {
            get() {
                return this.draft.date_from || this.draft.date_to
                    ? [this.draft.date_from, this.draft.date_to]
                    : null;
            },
            set(value) {
                const v = Array.isArray(value) ? value : [];
                this.draft.date_from = v[0] || "";
                this.draft.date_to = v[1] || "";
            }
        },
        // Carriers seen by the BE (facets), plus any already picked from the URL
        // so a selected value never renders as a bare tag with no option.
        providerOptions() {
            const out = this.facetProviders.slice();
            this.draft.shipping_providers.forEach(p => {
                if (out.indexOf(p) < 0) out.push(p);
            });
            return out;
        },
        // Payment-method types straight from the shared store getter, so the ids
        // stay pinned to controllers/const.go:199-206 in exactly one place. Only
        // id 0's wording changes: here it is a real default meaning "every type".
        paymentTypeStore() {
            return this.$store.getters["PaymentMethod/getPaymentMethodTypeTableWithAll"] || [];
        },
        paymentTypeChoices() {
            return paymentTypeOptions(this.paymentTypeStore, this.$t("seller_orders.all"));
        },
        previewChipList() {
            return previewChips(this.preview);
        },
        cutOffRows() {
            return previewRows(this.cutOffPreview);
        },
        cutOffTotal() {
            return this.cutOffPreview.present ? this.cutOffPreview.totalCount : 0;
        }
    },
    created() {
        this.restoreFromQuery(this.$route.query);
        // Owner 2026-10-20 (spec awards-i18n R6): first load opens on the last
        // 7 Bangkok days unless the URL already carries a date or a search
        // term (a deep-link search must still find an old order). Only here —
        // a cleared range is written to the URL without dates, and the query
        // watcher below never re-applies the default (no snap-back).
        if (shouldApplyDefaultDate(this.$route.query, ORDER_SEARCH_PARAMS)) {
            this.filters = cloneFilters(withDefaultDateRange(this.filters, ORDERS_DEFAULT_DAYS, this.$route.query));
            this.syncQuery();
        }
    },
    mounted() {
        this.search();
        window.addEventListener("resize", this.measureTable);
    },
    watch: {
        // Same path, new query (menu link, browser back between our own
        // states): re-read the URL unless it is the state we just wrote.
        "$route.query"(query) {
            const want = JSON.stringify(queryFromFilters(this.tab, this.filters, this.page));
            const parsed = filtersFromQuery(query);
            const got = JSON.stringify(queryFromFilters(parsed.tab, parsed.filters, parsed.page));
            if (want === got) return;
            this.restoreFromQuery(query);
            this.search();
        }
    },
    beforeDestroy() {
        if (this.previewTimer) clearTimeout(this.previewTimer);
        window.removeEventListener("resize", this.measureTable);
    },
    methods: {
        queryParams() {
            // The preview block in the response is counted with these two, so
            // the chip total equals what the cut will move (design 2026-10-04
            // §4.1). payment_method_type doubles as the list filter (2026-10-05
            // §2 item 6).
            return apiParams(
                this.tab,
                this.filters,
                this.page,
                this.limit,
                previewQueryParams({
                    backDay: this.cutOff.back_day,
                    paymentMethodType: this.filters.payment_method_type
                })
            );
        },
        restoreFromQuery(query) {
            const parsed = filtersFromQuery(query);
            this.tab = parsed.tab;
            this.loadedTab = parsed.tab;
            this.filters = parsed.filters;
            this.page = parsed.page;
        },
        syncQuery() {
            this.$router
                .replace({ query: queryFromFilters(this.tab, this.filters, this.page) })
                .catch(() => {});
        },
        async search() {
            this.loading = true;
            // A new page of rows: every open panel closes and its cache goes.
            this.clearDetails();
            try {
                const res = await SellerService.orders(this.queryParams());
                const data = (res.data && res.data.data) || {};
                this.rows = data.rows || [];
                this.total = data.total || 0;
                this.tabCounts = normalizeTabCounts(data.tab_counts);
                this.statusCounts = normalizeStatusCounts(data.status_counts);
                this.truncatedWms = data.truncated_wms === true;
                this.preview = previewFromListResponse(res);
            } catch (err) {
                this.preview = normalizePreview(null, { present: false });
                swal.fire({
                    icon: "error",
                    title: this.$t("seller_orders.load_error"),
                    text: apiErrorMessage(err, this.$t)
                });
            } finally {
                this.loading = false;
            }
        },
        // el-tabs has already written `tab` through v-model when this fires;
        // a click on the tab already shown is ignored.
        onTabClick() {
            if (this.tab === this.loadedTab) return;
            this.loadedTab = this.tab;
            // A sub-status of the old tab means nothing on the new one (§5.4).
            this.filters = Object.assign(cloneFilters(this.filters), { status: "" });
            this.page = 1;
            this.syncQuery();
            this.search();
        },
        onPageChange(p) {
            this.page = p;
            this.syncQuery();
            this.search();
        },
        openFilter() {
            this.draft = cloneFilters(this.filters);
            this.filterVisible = true;
            this.loadFacets();
        },
        clearDraft() {
            this.draft = emptyFilters();
        },
        applyFilter() {
            this.filters = cloneFilters(this.draft);
            this.page = 1;
            this.filterVisible = false;
            this.syncQuery();
            this.search();
        },
        onRemoveChip(key) {
            this.filters = removeChip(this.filters, key);
            this.page = 1;
            this.syncQuery();
            this.search();
        },
        // Clears every filter but keeps the tab (§5.1 item 2).
        resetFilters() {
            this.filters = emptyFilters();
            this.page = 1;
            this.syncQuery();
            this.search();
        },
        // Carrier names for the panel, asked for once (design §3.1: facets only
        // with with_facets=1) and cached. limit 1: only the facets are wanted.
        async loadFacets() {
            if (this.facetsLoaded || this.facetsLoading) return;
            this.facetsLoading = true;
            try {
                // No `tab`, so the BE skips the tab counts for this call.
                const res = await SellerService.orders(
                    Object.assign({ page: 1, limit: 1, with_facets: 1 }, previewQueryParams({}))
                );
                const data = (res.data && res.data.data) || {};
                const list = data.facets && Array.isArray(data.facets.shipping_providers) ? data.facets.shipping_providers : [];
                this.facetProviders = list.map(x => String(x || "").trim()).filter(Boolean);
                this.facetsLoaded = true;
            } catch (err) {
                // Non-fatal: the carrier select just has no suggestions.
                this.facetProviders = [];
            } finally {
                this.facetsLoading = false;
            }
        },
        tabLabelKey(key) {
            return `seller_orders.tab_${key}`;
        },
        formatTabCount(n) {
            return formatTabCount(n);
        },
        statusBadgeClass(code) {
            return statusBadgeClass(code);
        },
        // Owner 2026-10-15: the order number opens / closes the detail under
        // its row (no modal). Several rows may be open at once.
        toggleDetail(row) {
            const table = this.$refs.ordersTable;
            if (table) table.toggleRowExpansion(row);
        },
        closeDetail(row) {
            const table = this.$refs.ordersTable;
            if (table) table.toggleRowExpansion(row, false);
        },
        isExpanded(row) {
            return !!row && this.expandedUuids.indexOf(row.uuid) > -1;
        },
        // el-table emits (row, expandedRows) after every toggle.
        onExpandChange(row, expandedRows) {
            const list = Array.isArray(expandedRows) ? expandedRows : [];
            this.expandedUuids = list.map(r => r && r.uuid).filter(Boolean);
            if (!row || this.expandedUuids.indexOf(row.uuid) < 0) return;
            this.measureTable();
            if (this.detailByUuid[row.uuid] || this.detailLoading[row.uuid]) return;
            this.loadDetail(row);
        },
        // GET /api/v1/bo/seller/orders/:uuid once per row; a failure keeps the
        // panel open with a retry button.
        async loadDetail(row) {
            const uuid = row && row.uuid;
            if (!uuid) return;
            const seq = this.fetchSeq;
            this.$set(this.detailLoading, uuid, true);
            this.$set(this.detailError, uuid, "");
            try {
                const res = await SellerService.orderDetail(uuid);
                if (seq !== this.fetchSeq) return; // the list was reloaded meanwhile
                const data = (res && res.data && res.data.data) || {};
                this.$set(this.detailByUuid, uuid, data && typeof data === "object" ? data : {});
            } catch (err) {
                if (seq !== this.fetchSeq) return;
                const msg = apiErrorMessage(err, this.$t) || this.$t("seller_orders.load_error");
                this.$set(this.detailError, uuid, msg);
            } finally {
                if (seq === this.fetchSeq) this.$set(this.detailLoading, uuid, false);
            }
        },
        clearDetails() {
            this.fetchSeq += 1;
            this.detailByUuid = {};
            this.detailLoading = {};
            this.detailError = {};
            this.expandedUuids = [];
            // Element 2.4 keeps a row-key's expansion across new data: close them.
            const table = this.$refs.ordersTable;
            if (table && table.store && typeof table.store.setExpandRowKeys === "function") table.store.setExpandRowKeys([]);
        },
        measureTable() {
            const table = this.$refs.ordersTable;
            const el = table && table.$el;
            this.tableViewWidth = el && el.clientWidth ? el.clientWidth : 0;
        },
        trackingNumbersOf(row) {
            return trackingNumbersOf(row);
        },
        // ---- who cancelled + seller cancel (spec 2026-10-16 §4/§5) ----
        // The `cancel` object of a cancelled (140) row; null otherwise / older API.
        cancelOf(row) {
            if (!row || Number(row.order_status) !== 140) return null;
            return row.cancel && typeof row.cancel === "object" ? row.cancel : null;
        },
        cancelBadgeOf(row) {
            return cancelBadge(this.cancelOf(row), (k, p) => this.$t(k, p));
        },
        // Tooltip: reason type · reason (or staff note) · time; "" when none.
        cancelTooltipOf(row) {
            const v = cancelView(this.cancelOf(row), (k, p) => this.$t(k, p));
            if (!v) return "";
            return [v.typeLabel, v.reasonText, v.at].filter(Boolean).join(" · ");
        },
        canCancelRow(row) {
            return canSellerCancel(row, row && this.detailByUuid[row.uuid], this.isFullOrder === true);
        },
        openCancel(row) {
            if (!row) return;
            this.cancelTarget = row;
            this.cancelForm = emptyCancelForm();
            this.cancelVisible = true;
        },
        onCancelSubmit(form) {
            this.cancelForm = Object.assign(emptyCancelForm(), form || {});
            return this.submitCancel();
        },
        // Same checks + swals as the BO cancel modal (OrderTables.vue
        // handelUpdateCancel), then a confirm, then POST; the POST re-checks
        // everything server side (403 / 404 / 409 come back as a message).
        async submitCancel() {
            const row = this.cancelTarget;
            if (this.cancelSubmitting || !row || !row.uuid) return;
            const errKey = validateCancelForm(this.cancelForm);
            if (errKey) {
                swal.fire({ icon: "error", title: this.$t(errKey), text: "" });
                return;
            }
            const order = row.order_no || row.uuid;
            const confirmed = await swal.fire({
                icon: "warning",
                title: this.$t("seller_orders.cancel_confirm_title"),
                text: this.$t("seller_orders.cancel_confirm_text", { order }),
                showCancelButton: true,
                confirmButtonText: this.$t("seller_orders.cancel_confirm_btn"),
                cancelButtonText: this.$t("seller_orders.cancel_close")
            });
            if (!confirmed || !confirmed.value) return;

            this.cancelSubmitting = true;
            try {
                await SellerService.cancelOrder(row.uuid, cancelPayload(this.cancelForm));
                this.cancelVisible = false;
                const reopen = this.isExpanded(row);
                swal.fire({ icon: "success", title: this.$t("seller_orders.cancel_success"), text: "" });
                await this.search();
                if (reopen) this.restoreDetail(row.uuid);
            } catch (err) {
                swal.fire({
                    icon: "error",
                    title: this.$t("seller_orders.cancel_error"),
                    // vue-i18n's $t reads this.$i18n: handed over bare it loses `this`.
                    text: apiErrorMessage(err, this.$t.bind(this))
                });
            } finally {
                this.cancelSubmitting = false;
            }
        },
        // After the reload: open the cancelled order's panel again (fresh detail).
        restoreDetail(uuid) {
            return this.$nextTick(() => {
                const row = this.rows.find(r => r && r.uuid === uuid);
                const table = this.$refs.ordersTable;
                if (row && table) table.toggleRowExpansion(row, true);
            });
        },
        async copyText(value) {
            const text = clipboardText(value);
            if (!text) return false;
            const ok = await copyToClipboard(text);
            this.$notify({
                type: ok ? "success" : "danger",
                message: ok ? this.$t("seller_orders.copied", { value: text }) : this.$t("seller_orders.copy_failed")
            });
            return ok;
        },
        async openCutOff() {
            this.cutOffVisible = true;
            // A window the server would silently rewrite must never be shown as
            // if it were the one being counted (design §4.1, AC 9).
            this.cutOff.back_day = clampBackDay(this.cutOff.back_day);
            await this.loadPreview();
        },
        // Both modal selects land here; 300ms of damping keeps a quick 1 -> 30
        // spin from firing a request per intermediate value (design §3 step 5).
        onCutOffParamsChange() {
            if (this.previewTimer) clearTimeout(this.previewTimer);
            this.previewTimer = setTimeout(() => {
                this.previewTimer = null;
                this.loadPreview();
            }, PREVIEW_DEBOUNCE_MS);
        },
        // Counts for the modal, read from the list endpoint's `packing_preview`
        // with limit 1 — only the preview block is wanted, not another page of
        // rows. A failure leaves `present: false`, which disables the confirm
        // button rather than showing a confident 0.
        async loadPreview() {
            this.previewLoading = true;
            try {
                const res = await SellerService.orders(
                    Object.assign(
                        { page: 1, limit: 1 },
                        previewQueryParams({
                            backDay: this.cutOff.back_day,
                            paymentMethodType: this.cutOff.bo_payment_method_type_id
                        })
                    )
                );
                this.cutOffPreview = previewFromListResponse(res);
            } catch (err) {
                this.cutOffPreview = normalizePreview(null, { present: false });
                this.$notify({
                    type: "danger",
                    message: this.$t("seller_orders.preview_error") + " " + apiErrorMessage(err, this.$t)
                });
            } finally {
                this.previewLoading = false;
            }
        },
        async submitCutOff() {
            const cnt = this.cutOffTotal;
            if (cnt === 0) return;
            const confirmed = await swal.fire({
                icon: "warning",
                title: this.$t("seller_orders.cutoff_title_all"),
                text: this.$t("seller_orders.cutoff_confirm_text", { cnt }),
                showCancelButton: true,
                confirmButtonText: this.$t("seller_orders.btn_confirm_cut"),
                cancelButtonText: this.$t("seller_orders.btn_cancel")
            });
            if (!confirmed || !confirmed.value) return;

            this.cutOffLoading = true;
            try {
                const res = await SellerService.packingRound(
                    packingRoundPayload({
                        backDay: this.cutOff.back_day,
                        paymentMethodType: this.cutOff.bo_payment_method_type_id
                    })
                );
                const result = packingRoundResult(res);
                this.cutOffVisible = false;
                // Orders with no warehouse mapping ARE in the round (owner
                // decision, design §9 item 1) — say so, loudly, or they sit in
                // the pack room looking already-packed with nobody told.
                if (result.noWmsSummaryCount > 0) {
                    this.$notify({
                        type: "warning",
                        message: this.$t("seller_orders.cutoff_warn_no_wms", {
                            cnt: result.noWmsSummaryCount
                        })
                    });
                }
                const done = await swal.fire({
                    icon: "success",
                    title: this.$t("seller_orders.cutoff_success_title"),
                    text: this.$t("seller_orders.cutoff_success_all", successMessageArgs(result)),
                    showCancelButton: true,
                    confirmButtonText: this.$t("seller_orders.btn_go_packing"),
                    cancelButtonText: this.$t("seller_orders.btn_cancel")
                });
                if (this.$refs.trackingRecovery) this.$refs.trackingRecovery.scheduleRefetch();
                if (done && done.value && result.packingUrl) {
                    this.$router.push(result.packingUrl);
                    return;
                }
                this.search();
            } catch (err) {
                // The body normally carries the i18n key in `Msg`; the mapped
                // key is the floor for a 409/422 that arrived with an empty body.
                const key = packingRoundErrorKey(err);
                const fallback = key ? this.$t(key) : "";
                const text = apiErrorMessage(err, this.$t);
                swal.fire({
                    icon: "error",
                    title: this.$t("seller_orders.cutoff_error"),
                    text: fallback && fallback !== key ? fallback : text
                });
            } finally {
                this.cutOffLoading = false;
            }
        },
        goPackingRoom(boPackingId) {
            const id = Number(boPackingId || 0);
            if (!id) return;
            this.$router.push(`/backoffice/packings?bo_packing_id=${id}`);
        },
        channelBadgeOf(row) {
            return channelBadge((row && (row.source_channel || row.channel)) || "");
        },
        // Owner 2026-10-15: 2C2P card orders (type 3) read "บัตรเครดิต/เดบิต
        // (2C2P)" — the shared store list has no id 3, so the cell was "-".
        paymentTypeLabel(id, ownName) {
            return sellerPaymentTypeLabel(id, this.paymentTypeStore, key => this.$t(key), ownName);
        },
        // Owner 2026-10-26: name the REAL 2C2P channel (Thai QR, TrueMoney, card
        // scheme, KBANK 123 …) from 2C2P's notice; the type label otherwise.
        paymentTypeOf(row) {
            return (
                sellerPaymentChannelLabel(row, this.$i18n && this.$i18n.locale) ||
                this.paymentTypeLabel(row && row.payment_method_type, row && row.payment_method_name)
            );
        },
        shortUuid(uuid) {
            if (!uuid) return "";
            return uuid.length > 8 ? uuid.slice(0, 8) : uuid;
        },
        copyUuid(uuid) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(uuid);
            }
        },
        orderStatusLabel(status) {
            const key = `seller_orders.status_${status}`;
            const label = this.$t(key);
            return label === key ? String(status) : label;
        },
        payStatusLabel(status) {
            switch (status) {
                case 1:
                    return this.$t("seller_orders.pay_success");
                case 2:
                    return this.$t("seller_orders.pay_in_progress");
                case 3:
                    return this.$t("seller_orders.pay_failed");
                default:
                    return this.$t("seller_orders.pay_none");
            }
        },
        payStatusClass(status) {
            switch (status) {
                case 1:
                    return "badge-success";
                case 2:
                    return "badge-warning";
                case 3:
                    return "badge-danger";
                default:
                    return "badge-secondary";
            }
        },
        formatAmount(value) {
            const n = Number(value || 0);
            return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatTime(value) {
            if (!value) return "";
            const d = new Date(value);
            if (isNaN(d.getTime())) return "";
            const pad = n => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        }
    }
};
</script>

<style>
/* design 2026-10-05 §5.2 — el-dialog drawn as a right-side panel (Element UI
   2.4.11 has no el-drawer). Unscoped: the dialog is appended to <body>. */
.seller-filter-drawer {
    position: fixed;
    right: 0;
    top: 0;
    margin: 0 !important;
    height: 100vh;
    display: flex;
    flex-direction: column;
    border-radius: 0;
}
.seller-filter-drawer .el-dialog__body {
    flex: 1;
    overflow-y: auto;
    padding-top: 8px;
}
.seller-filter-drawer .sf-field {
    margin-bottom: 14px;
}
.seller-filter-drawer .sf-label {
    font-size: 0.8rem;
    color: #525f7f;
    margin-bottom: 4px;
}
.seller-filter-drawer .sf-radios .el-radio {
    display: block;
    margin: 0 0 8px 0;
}
.seller-filter-drawer .sf-radios-inline .el-radio {
    display: inline-block;
    margin-right: 16px;
}
@media (max-width: 575.98px) {
    .seller-filter-drawer {
        width: 100% !important;
    }
}
/* Owner 2026-10-05: the theme .badge is ~66% font-size — too small to read in
   the ช่องทาง / สถานะ / สถานะชำระเงิน / ออเดอร์ BO columns. Colours still come
   from the badge-* classes. Class name is page-specific, so unscoped is safe. */
.badge.so-badge-lg {
    font-size: 0.8rem;
    padding: 0.4em 0.65em;
    /* Kanit 500 still reads as bold for Thai glyphs — owner wants the same
       weight as the digits in the ออเดอร์ BO badge, i.e. regular. */
    font-weight: 400;
}
.seller-filter-toolbar .seller-period-chip {
    cursor: default;
}
.seller-status-tabs .tab-count {
    color: #324cdd;
    margin-left: 2px;
}
/* Owner 2026-10-15: the expand column only carries the inline detail — no
   arrow, no padding (same as OrdersNew.vue's bon-expand-col). */
.so-expand-col .cell,
.so-expand-col .el-table__expand-icon {
    display: none;
}
th.so-expand-col,
.card .el-table td.so-expand-col,
.card .el-table th.so-expand-col {
    padding: 0;
}
.card .so-orders-table td.el-table__expanded-cell {
    padding: 0;
}
.so-orders-table .so-detail-sticky {
    position: sticky;
    left: 0;
    max-width: 100%;
}
.so-orders-table .so-order-link {
    white-space: nowrap;
}
.so-orders-table .so-order-chevron {
    font-size: 0.7rem;
}
/* "ยกเลิกโดยพนักงาน (ชื่อ)" may be long: wrap inside the 160px status column. */
.so-orders-table .so-cancel-badge {
    white-space: normal;
    /* the theme cell is break-all: Thai would split mid-word ("พนักงา|น") */
    word-break: normal;
    overflow-wrap: break-word;
    text-align: left;
    line-height: 1.35;
    cursor: default;
}
.so-orders-table .so-tracking {
    white-space: nowrap;
}
/* "บัตรเครดิต/เดบิต (2C2P)" wraps between words, not mid-word (the theme's
   cell is break-all). */
.so-orders-table td.so-words .cell {
    word-break: normal;
    overflow-wrap: break-word;
}
.so-orders-table .so-copy {
    color: #525f7f;
    font-size: 0.8rem;
    vertical-align: baseline;
    line-height: 1;
}
</style>
<style scoped>
/* spec 2026-10-07 §11.2 R15: the range picker spans the panel on its own line. */
.sf-date-range {
    width: 100% !important;
}
</style>
